import type { ChartData, EmailData } from 'shared/types/agent-message-content';

export type ChatEventKind =
    | 'thinking'
    | 'tool'
    | 'result'
    | 'error'
    | 'aborted'
    | 'start'
    | 'sdk_event'
    | 'compact_boundary';

export interface ChatEvent {
    id?: string;
    ts: number;
    kind: ChatEventKind;
    label: string;
    details?: string;
    status?: string;
    durationMs?: number;
    showInTranscript: boolean;
}

// Table data structures for displaying tabular results in chat
export interface TableData {
    title: string; // MANDATORY - plain text description of the table
    columns: TableColumn[]; // At least 1 column required
    rows: TableRow[]; // At least 1 row required (no empty tables)
    sortableColumns?: string[]; // Optional: column IDs that can be sorted by user
}

export interface TableColumn {
    id: string;
    label: string;
}

export interface TableRow {
    [columnId: string]: CellValue;
    // Every row MUST have a value for each column (can be null/undefined)
}

export type CellValue = string | number | boolean | null | undefined | CellWithEntity;

// Discriminated union for type-safe entity IDs
export type CellWithEntity = CellWithSingleEntity | CellWithCandidateEntity;

export interface CellWithSingleEntity {
    displayValue: string | number;
    entityType: 'person' | 'job' | 'client' | 'company';
    entityId: string;
}

export interface CellWithCandidateEntity {
    displayValue: string | number;
    entityType: 'candidate';
    entityId: { personId: string; jobId: string };
}

// Type guards for table data
export function isCellWithEntity(value: CellValue): value is CellWithEntity {
    return typeof value === 'object' && value !== null && 'displayValue' in value;
}

export function isCandidateEntity(cell: CellWithEntity): cell is CellWithCandidateEntity {
    return cell.entityType === 'candidate';
}

export function getCellDisplayValue(value: CellValue): string | number {
    if (isCellWithEntity(value)) {
        return value.displayValue;
    }
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    return value;
}

export type FeedItem =
    | { ts: number; kind: 'user_text'; content: string }
    | { ts: number; kind: 'assistant_text'; content: string }
    | { ts: number; kind: 'event'; event: ChatEvent }
    | { ts: number; kind: 'table'; table: TableData }
    | { ts: number; kind: 'chart'; chart: ChartData }
    | { ts: number; kind: 'email_draft'; draftEmailId: string; linkType?: string; stageIndex?: number }
    | { ts: number; kind: 'submission_draft'; draftSubmissionId: string }
    | { ts: number; kind: 'client_update_draft'; draftClientUpdateId: string }
    | { ts: number; kind: 'job_update_draft'; draftJobUpdateId: string }
    | { ts: number; kind: 'search_preview'; searchDraftId: string };

// re-export ChartData and EmailData for client side usage
export type { ChartData, EmailData };
